import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import ResearchComparisonWorkspace, { BackToChatButton } from '../components/ResearchComparison';
import { getResearchComparisonCapabilities } from '../services/localResearchComparison';
import type { ResearchComparisonCapabilities } from '../types/localResearchComparison';

/**
 * Die Karte vor dem Arbeitsplatz: der Vergleich wird geprüft, oder er steht
 * nicht zur Verfügung. Beide Zustände führen sichtbar in den Chat zurück --
 * sonst bliebe, wem der Vergleich ausfällt, auf einer Seite ohne Ausgang.
 */
export const ComparisonStatusCard: React.FC<{ error: string | null }> = ({ error }) => (
  <div className="flex h-full items-center justify-center bg-warm-bg p-6 text-primary">
    <div className="rounded max-w-lg border border-warm-200 bg-warm-card p-8 text-center shadow-sm">
      {error ? (
        <>
          <AlertTriangle className="mx-auto h-7 w-7 text-accent" />
          <h1 className="mt-4 font-serif text-2xl">Vergleich nicht verfügbar</h1>
          <p className="mt-3 text-sm leading-6 text-muted">{error}</p>
        </>
      ) : (
        <>
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-accent" />
          <p className="mt-4 font-serif text-xl">Varianten werden geprüft …</p>
        </>
      )}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
        {error && (
          <button type="button" onClick={() => window.location.reload()} className="rounded-[3px] bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-dark">
            Erneut versuchen
          </button>
        )}
        <BackToChatButton />
      </div>
    </div>
  </div>
);

const ResearchComparison: React.FC = () => {
  const [capabilities, setCapabilities] = useState<ResearchComparisonCapabilities | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    getResearchComparisonCapabilities(controller.signal)
      .then(result => {
        if (!result.enabled) {
          setError(result.reason || 'Der Vergleich ist in dieser Laufzeit nicht freigeschaltet.');
          return;
        }
        setCapabilities(result);
      })
      .catch(cause => {
        if (!controller.signal.aborted) {
          setError(cause instanceof Error ? cause.message : 'Der Vergleichsstatus ist nicht erreichbar.');
        }
      });
    return () => controller.abort();
  }, []);

  if (capabilities) {
    return <ResearchComparisonWorkspace capabilities={capabilities} />;
  }

  return <ComparisonStatusCard error={error} />;
};

export default ResearchComparison;
